// Copyright (c) 2026, INDOPAR and contributors
//
// The account control in the marketing site's nav and footer.
//
// Rendered here rather than on the server, and the reason matters: Frappe caches
// website pages under a key of path and language only — not the visitor. A name
// baked into the navbar server-side would be served to whoever asked for that page
// next, including a guest. Nothing user-specific may go into that HTML.
//
// So the markup ships a neutral placeholder and this fills it in from the cookies
// Frappe sets at login (full_name, user_id, user_image, system_user). They are
// per-browser, so a cached page carries no identity and the control is still right
// for whoever is looking at it. It also costs no request.

(function () {
	function cookie(name) {
		const hit = document.cookie.split("; ").find((row) => row.startsWith(name + "="));
		if (!hit) return "";
		let value = hit.slice(name.length + 1);
		try {
			value = decodeURIComponent(value);
		} catch (e) {
			// A cookie we cannot decode is one we do not use.
			return "";
		}
		// Frappe quotes values containing spaces.
		return value.replace(/^"|"$/g, "").trim();
	}

	function initials(name, email) {
		const source = (name || email || "").trim();
		if (!source) return "?";
		const parts = source.split(/[\s@._-]+/).filter(Boolean);
		return (
			(parts[0] || "")[0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")
		).toUpperCase();
	}

	function esc(value) {
		const d = document.createElement("div");
		d.textContent = value == null ? "" : String(value);
		return d.innerHTML;
	}

	function session() {
		const user = cookie("user_id");
		// "Guest" is a signed-in session too, as far as Frappe is concerned. It is not
		// one as far as anybody reading this page is concerned.
		if (!user || user === "Guest") return null;
		return {
			user: user,
			name: cookie("full_name") || user,
			image: cookie("user_image"),
			// Only a System User has anywhere to go in the Desk.
			desk: cookie("system_user") === "yes",
		};
	}

	// The control's words, in the page's language: the nav renders them into
	// #gfAccountLabels, since this file has no translations of its own. English when absent.
	// Read when the control is drawn, not when this file loads: it loads in the <head>,
	// before the nav that carries them.
	let cached = null;
	function labels() {
		if (cached) return cached;
		const fallback = {
			login: "Log in",
			logout: "Log out",
			sites: "My sites",
			billing: "Plan &amp; billing",
			support: "Support",
			desk: "Desk",
		};
		try {
			const el = document.getElementById("gfAccountLabels");
			const given = el ? JSON.parse(el.textContent) || {} : {};
			Object.keys(given).forEach((k) => (given[k] = esc(given[k])));
			cached = Object.assign(fallback, given);
		} catch (e) {
			cached = fallback;
		}
		return cached;
	}

	function avatar(me) {
		if (me.image) {
			return `<img class="gfa-face" src="${encodeURI(me.image)}" alt="">`;
		}
		return `<span class="gfa-face gfa-initials">${esc(initials(me.name, me.user))}</span>`;
	}

	function menu(me) {
		const item = (href, label) => `<li><a href="${href}">${label}</a></li>`;
		return `
			<div class="gfa-head">
				<div class="gfa-name">${esc(me.name)}</div>
				<div class="gfa-email">${esc(me.user)}</div>
			</div>
			<ul class="gfa-list">
				${item("/dashboard", labels().sites)}
				${item("/dashboard/plan", labels().billing)}
				${item("/helpdesk", labels().support)}
				${me.desk ? item("/app", labels().desk) : ""}
			</ul>
			<ul class="gfa-list gfa-out">
				${item("/logout", labels().logout)}
			</ul>`;
	}

	function renderNav(host, me) {
		if (!me) {
			host.innerHTML = `<a class="gfa-login" href="/login">${labels().login}</a>`;
			return;
		}
		host.innerHTML = `
			<button class="gfa-btn" type="button" aria-haspopup="true" aria-expanded="false"
				aria-label="${esc(me.name)}">${avatar(me)}</button>
			<div class="gfa-menu" hidden>${menu(me)}</div>`;

		const button = host.querySelector(".gfa-btn");
		const panel = host.querySelector(".gfa-menu");

		const close = () => {
			panel.hidden = true;
			button.setAttribute("aria-expanded", "false");
		};
		button.addEventListener("click", (event) => {
			event.stopPropagation();
			const open = panel.hidden;
			panel.hidden = !open;
			button.setAttribute("aria-expanded", open ? "true" : "false");
		});
		document.addEventListener("click", close);
		document.addEventListener("keydown", (event) => {
			if (event.key === "Escape") close();
		});
		panel.addEventListener("click", (event) => event.stopPropagation());
	}

	function renderFoot(host, me) {
		host.innerHTML = me
			? `<a href="/dashboard">${esc(
					me.name
			  )}</a><span class="gfa-sep">·</span><a href="/logout">${labels().logout}</a>`
			: `<a href="/login">${labels().login}</a>`;
	}

	// -- language ------------------------------------------------------------------
	//
	// The picker shipped from the page builder with every entry pointing at "#", so
	// it opened, looked right, and did nothing. Each one now goes to the endpoint
	// that sets the language and comes back to this page.

	// Drawn rather than typed. Emoji flags are a pair of regional-indicator letters
	// that each platform renders as it pleases: Windows shows the bare letters "GB",
	// and where they do render they come out smaller than the text beside them and
	// shaped like the emoji font rather than the page. These are flat, crisp at any
	// size, and set to the height of the words they sit next to.
	function flagSvg(paths) {
		return (
			'<svg class="gf-flag" viewBox="0 0 60 40" aria-hidden="true" focusable="false">' +
			paths +
			"</svg>"
		);
	}

	const FLAGS = {
		gb:
			'<rect width="60" height="40" fill="#012169"/>' +
			'<path d="M0 0l60 40M60 0L0 40" stroke="#fff" stroke-width="9"/>' +
			'<path d="M0 0l60 40M60 0L0 40" stroke="#C8102E" stroke-width="4"/>' +
			'<path d="M30 0v40M0 20h60" stroke="#fff" stroke-width="14"/>' +
			'<path d="M30 0v40M0 20h60" stroke="#C8102E" stroke-width="8"/>',
		es:
			'<rect width="60" height="40" fill="#AA151B"/>' +
			'<rect y="10" width="60" height="20" fill="#F1BF00"/>',
		br:
			'<rect width="60" height="40" fill="#009B3A"/>' +
			'<path d="M30 4 56 20 30 36 4 20z" fill="#FEDF00"/>' +
			'<circle cx="30" cy="20" r="8.5" fill="#002776"/>',
		cn:
			'<rect width="60" height="40" fill="#DE2910"/>' +
			'<path fill="#FFDE00" d="M13 4l1.8 5.53h5.81l-4.7 3.41 1.79 5.53L13 15.06l-4.7 3.41 1.79-5.53-4.7-3.41h5.81z"/>' +
			'<circle cx="26" cy="7" r="1.7" fill="#FFDE00"/>' +
			'<circle cx="31" cy="12" r="1.7" fill="#FFDE00"/>' +
			'<circle cx="31" cy="19" r="1.7" fill="#FFDE00"/>' +
			'<circle cx="26" cy="24" r="1.7" fill="#FFDE00"/>',
	};

	const LANGUAGES = [
		{ code: "en", flag: flagSvg(FLAGS.gb), label: "English" },
		{ code: "es", flag: flagSvg(FLAGS.es), label: "Espa\u00f1ol" },
		{ code: "pt-BR", flag: flagSvg(FLAGS.br), label: "Portugu\u00eas" },
		{ code: "zh", flag: flagSvg(FLAGS.cn), label: "\u4e2d\u6587" },
	];

	function activeLanguage() {
		// What the server actually rendered in, not what we last asked for.
		const lang = (document.documentElement.getAttribute("lang") || "").trim();
		return (
			LANGUAGES.find((l) => l.code === lang) ||
			LANGUAGES.find((l) => l.code.split("-")[0] === lang.split("-")[0]) ||
			LANGUAGES[0]
		);
	}

	/* The language lives in the URL, not in a cookie.

	   This used to point at /api/method/...set_language, which set a cookie and sent you
	   back to the *English* path. Two problems: the site now serves /es/<route> with its
	   own canonical and hreflang, so a cookie-language on an English URL contradicts what
	   the page tells a crawler; and gfc_site.bundle.js rewrites the same menu to the
	   prefixed URLs, so whichever script painted last won and the switcher behaved
	   differently depending on when the session fetch resolved.

	   Both scripts now produce identical hrefs, so it no longer matters who wins.
	   `set_language` still exists for anything that genuinely wants to persist a
	   preference against the user record; the marketing switcher does not. */
	const LOCALE_PREFIX = { en: "", es: "es", "pt-BR": "pt", zh: "zh" };
	const LOCALE_PREFIXES = ["es", "pt", "zh"];

	function translatedRoutes() {
		// Rendered by templates/includes/marketing_nav.html from i18n.TRANSLATED_ROUTES.
		try {
			const el = document.getElementById("gfLocaleRoutes");
			return el ? JSON.parse(el.textContent) || [] : [];
		} catch (e) {
			return [];
		}
	}

	function languageHref(code) {
		const prefix = LOCALE_PREFIX[code] || "";
		let parts = window.location.pathname.split("/").filter(Boolean);
		if (LOCALE_PREFIXES.indexOf(parts[0]) >= 0) parts = parts.slice(1);
		const route = "/" + parts.join("/");
		const search = window.location.search || "";

		// Only routes that exist in every language may be prefixed. Anything else
		// (/login, /signup, the dashboard) stays where it is rather than 404ing.
		if (route !== "/" && translatedRoutes().indexOf(route) < 0) {
			return route + search;
		}
		if (!prefix) return (route === "/" ? "/" : route) + search;
		return (route === "/" ? "/" + prefix : "/" + prefix + route) + search;
	}

	function paintLanguages() {
		const active = activeLanguage();

		document.querySelectorAll(".lang .lang-menu").forEach(function (menu) {
			menu.innerHTML = LANGUAGES.map(function (l) {
				const on = l === active;
				return (
					'<li><a data-gfl="1" href="' +
					languageHref(l.code) +
					'"' +
					(on ? ' aria-current="true" style="font-weight:700"' : "") +
					">" +
					l.flag +
					" " +
					esc(l.label) +
					"</a></li>"
				);
			}).join("");
		});

		// The button should show where you are, not always the English flag.
		document.querySelectorAll(".lang .lang-btn").forEach(function (button) {
			button.innerHTML = active.flag + '<span class="gf-flag-caret">\u25be</span>';
		});

		// The footer lists the same languages as plain text. Same links, quieter.
		document.querySelectorAll("[data-gf-languages]").forEach(function (host) {
			host.innerHTML = LANGUAGES.map(function (l) {
				return (
					'<a data-gfl="1" href="' +
					languageHref(l.code) +
					'">' +
					l.flag +
					" " +
					esc(l.label) +
					"</a>"
				);
			}).join(" \u00b7 ");
		});
	}

	function paint() {
		const me = session();
		document.querySelectorAll("[data-gf-account]").forEach((host) => renderNav(host, me));
		document
			.querySelectorAll("[data-gf-account-foot]")
			.forEach((host) => renderFoot(host, me));
		paintLanguages();
	}

	// The dashboard is a single-page app: its topbar is replaced on every route
	// change, taking the account menu and language picker with it. Exposing the
	// painter lets it ask for them back rather than reimplementing either.
	window.gm = window.gm || {};
	window.gm.account = { paint: paint };

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", paint);
	} else {
		paint();
	}
})();
